import { DescriptorEntorno, Entorno } from './entorno.model';

/**
 * Tiñe el favicon del color del entorno, para distinguir la pestaña **sin leer nada**.
 *
 * <h3>Se dibuja uno nuevo, no se recolorea el de marca</h3>
 * `favicon.svg` es el logo con los `fill` en rojo dentro; recolorearlo pediría descargarlo y
 * reescribirlo, o sea un `fetch` antes de poder pintar. Y a 16 px, que es donde se juega esto, el
 * logo no se distingue de todos modos: lo que se ve es una mancha de color. Es la misma conclusión a
 * la que llegó el icono de escritorio (`scripts/make-dev-icon.ps1`: «el color de fondo es el
 * diferenciador de verdad; el texto solo se dibuja de 48 px para arriba»). Así que se dibuja una
 * pastilla del color del entorno con su inicial, y en producción no se toca nada.
 *
 * <h3>El color se lee del TOKEN, no se escribe aquí</h3>
 * `check:color` prohíbe literales de color fuera de `tokens.scss` (`scripts/check-color-tokens.mjs`)
 * y hace bien: un hex repetido aquí sería el tercer sitio donde vive el violeta de desarrollo, y el
 * primero en desincronizarse.
 */

const TOKEN_POR_GRAVEDAD = {
  trasteable: '--color-entorno-trasteable',
  'datos-reales': '--color-entorno-datos-reales',
} as const;

/** Una letra por entorno. A 16 px casi no se lee; el color hace el trabajo y esto desempata. */
const INICIAL: Record<Entorno, string> = {
  produccion: '',
  desarrollo: 'D',
  local: 'L',
  slot: 'S',
  preprod: 'P',
  'no-identificado': '?',
};

export function aplicarFaviconEntorno(documento: Document, descriptor: DescriptorEntorno): void {
  if (descriptor.gravedad === 'ninguna') return;

  const color = colorDe(documento, TOKEN_POR_GRAVEDAD[descriptor.gravedad]);
  // Sin token resuelto (hojas aún no aplicadas, o un entorno de test sin estilos) se deja el favicon
  // de marca: mejor el de siempre que una pastilla negra que no significa nada.
  if (!color) return;

  documento.querySelectorAll('link[rel~="icon"]').forEach((link) => link.remove());

  const link = documento.createElement('link');
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  link.href = svgDataUri(color, INICIAL[descriptor.entorno]);
  documento.head.appendChild(link);
}

function colorDe(documento: Document, token: string): string {
  const vista = documento.defaultView;
  if (!vista) return '';
  return vista.getComputedStyle(documento.documentElement).getPropertyValue(token).trim();
}

function svgDataUri(color: string, inicial: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="7" fill="${color}"/>` +
    `<text x="16" y="23" text-anchor="middle" font-family="Segoe UI, Arial, sans-serif"` +
    ` font-size="21" font-weight="700" fill="#ffffff">${inicial}</text>` +
    `</svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
